import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Field } from "@/components/common";

function OtpStep({ state, onDone }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/mfa/verify", { mfa_token: state.mfa_token, code });
      onDone(data.user);
    } catch (err) {
      toast.error(errMsg(err));
      setCode("");
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-5" data-testid="mfa-form">
      {state.mfa_setup_required && (
        <div className="space-y-3" data-testid="mfa-setup-panel">
          <p className="text-sm text-muted-foreground">Administrators must enable two-step verification. Scan this QR code with Google Authenticator, Microsoft Authenticator or similar.</p>
          <div className="bg-white p-3 rounded-md w-fit mx-auto"><QRCodeSVG value={state.otpauth_uri} size={168} /></div>
          <p className="text-xs text-center text-muted-foreground">Or enter key manually: <span className="font-mono break-all" data-testid="mfa-secret">{state.secret}</span></p>
        </div>
      )}
      <Field label="6-digit authenticator code">
        <InputOTP maxLength={6} value={code} onChange={setCode} data-testid="mfa-code-input" autoFocus>
          <InputOTPGroup>{[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} className="h-12 w-11 text-lg" />)}</InputOTPGroup>
        </InputOTP>
      </Field>
      <Button type="submit" disabled={code.length !== 6 || busy} className="w-full h-11" data-testid="mfa-verify-btn">
        <ShieldCheck className="h-4 w-4 mr-2" />{busy ? "Verifying…" : "Verify & Sign in"}
      </Button>
    </form>
  );
}

export default function Login() {
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [mfa, setMfa] = useState(null);
  const [forgot, setForgot] = useState(false);
  const [portal, setPortal] = useState("staff");
  const admin = portal === "admin";

  const done = (u) => { setUser(u); nav("/"); };
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (forgot) {
        const { data } = await api.post("/auth/forgot-password", { email });
        toast.success(data.message);
        setForgot(false);
        return;
      }
      const { data } = await api.post("/auth/login", { email, password, portal });
      if (data.mfa_required || data.mfa_setup_required) setMfa(data);
      else done(data.user);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr]">
      <div className="hidden lg:flex relative bg-[#0B1530] text-white p-12 flex-col justify-between overflow-hidden">
        <img src="https://images.unsplash.com/photo-1687038520579-8d8f24721267?crop=entropy&cs=srgb&fm=jpg&q=70&w=1400" alt="" className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="relative flex items-center gap-3">
          <div className="h-10 w-10 rounded-md bg-red-600 grid place-items-center font-heading font-black text-xl">R</div>
          <span className="font-heading font-bold text-xl tracking-tight">ROXY CABLES</span>
        </div>
        <div className="relative max-w-md">
          <h1 className="font-heading text-4xl xl:text-5xl font-bold tracking-tight leading-[1.05]">Inventory, drums &amp; cable cutting — accounted to the metre.</h1>
          <p className="mt-4 text-slate-300">Private staff system · Bangalore</p>
        </div>
        <div className="relative text-xs text-slate-400">Authorised personnel only. All activity is logged.</div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-sm space-y-6">
          <div>
            <div className="lg:hidden flex items-center gap-2 mb-6">
              <div className="h-8 w-8 rounded-md bg-red-600 grid place-items-center font-heading font-black text-white">R</div>
              <span className="font-heading font-bold tracking-tight">ROXY CABLES</span>
            </div>
            <h2 className="font-heading text-2xl font-bold tracking-tight" data-testid="login-heading">{mfa ? "Two-step verification" : forgot ? "Reset password" : admin ? "Super Admin login" : "Staff login"}</h2>
            <p className="text-sm text-muted-foreground mt-1">{mfa ? "Enter the code from your authenticator app." : forgot ? "We'll email a reset link to the account's email and to the owner's backup email" : admin ? "Owner access · Inventory Management System" : "Warehouse, dispatch and office staff"}</p>
          </div>
          {!mfa && !forgot && (
            <div className="grid grid-cols-2 rounded-lg border p-1 bg-muted/50" role="tablist" data-testid="login-portal-tabs">
              {[["staff", "Staff login"], ["admin", "Super Admin login"]].map(([v, l]) => (
                <button key={v} type="button" role="tab" aria-selected={portal === v} onClick={() => { setPortal(v); setEmail(""); setPassword(""); }}
                  className={`h-9 rounded-md text-sm font-medium transition-colors ${portal === v ? (v === "admin" ? "bg-[#0B1530] text-white shadow" : "bg-red-600 text-white shadow") : "text-muted-foreground hover:text-foreground"}`}
                  data-testid={`login-tab-${v}`}>{l}</button>
              ))}
            </div>
          )}
          {mfa ? <OtpStep state={mfa} onDone={done} /> : (
            <form onSubmit={submit} className="space-y-4" data-testid="login-form">
              {forgot ? <Field label="Login ID or email"><Input type="text" inputMode="email" autoCapitalize="none" required value={email} onChange={(e) => setEmail(e.target.value)} className="h-11" data-testid="login-email-input" autoComplete="username" /></Field>
                : admin ? <Field label="Super Admin email"><Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="h-11" data-testid="login-email-input" autoComplete="username" /></Field>
                : <Field label="Login ID or email"><Input type="text" inputMode="email" autoCapitalize="none" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="e.g. roxydispatch" className="h-11" data-testid="login-email-input" autoComplete="username" /></Field>}
              {!forgot && <Field label="Password"><Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="h-11" data-testid="login-password-input" autoComplete="current-password" /></Field>}
              <Button type="submit" disabled={busy} className={`w-full h-11 ${!forgot && admin ? "bg-[#0B1530] hover:bg-[#0B1530]/90" : "bg-red-600 hover:bg-red-700"} text-white`} data-testid="login-submit-btn">{busy ? "Please wait…" : forgot ? "Request reset link" : admin ? "Sign in as Super Admin" : "Sign in"}</Button>
              <button type="button" className="text-xs text-muted-foreground hover:text-foreground underline-offset-4 hover:underline" onClick={() => setForgot(!forgot)} data-testid="forgot-password-toggle">
                {forgot ? "Back to sign in" : "Forgot password?"}
              </button>
            </form>
          )}
          <p className="text-[11px] text-muted-foreground"><Link to="/stock-view" className="underline font-medium" data-testid="login-public-stock-link">View live stock without login</Link> · No public registration. Accounts are created by the Super Admin. <Link to="/reset-password" className="underline">Have a reset link?</Link></p>
        </div>
      </div>
    </div>
  );
}
